/** 설정 화면 폼에서 다루는 프로필 필드의 정규화된 스냅샷입니다. */
export type ProfileFormSnapshot = {
	realName: string;
	countryId: number | null;
	phoneNumber: string;
	affiliation: string;
	position: string;
	department: string; // 레거시 필드 (호환용)
	jobTitle: string | null; // enum 값 또는 null
	customJobTitle: string; // 직무 직접입력
	profileImage: string | null;
};

export type PendingProfileFormSnapshot = {
	accountKey: string;
	form: ProfileFormSnapshot;
};

type ProfileLike = {
	realName?: string | null;
	countryId?: number | null;
	phoneNumber?: string | null;
	affiliation?: string | null;
	position?: string | null;
	department?: string | null; // 레거시 필드
	jobTitle?: string | null; // enum 값
	customJobTitle?: string | null; // 직무 직접입력
	profileImageUrl?: string | null;
};

const PENDING_PROFILE_FORM_STORAGE_KEY = 'medicrew_pending_profile_form';
const PROFILE_FORM_FALLBACK_STORAGE_KEY = 'medicrew_profile_form_fallback';
const TIME_ZONE_COUNTRY_CODE_BY_ID: Record<string, string> = {
	'Asia/Seoul': 'KR',
};
const LANGUAGE_COUNTRY_CODE_BY_LANGUAGE: Record<string, string> = {
	ko: 'KR',
};

/**
 * 계정 전환/프로필 재조회 타이밍에서 사용자가 방금 저장한 폼 값이 잠깐 사라지지 않도록
 * 메모리와 브라우저 저장소에 동일한 스냅샷을 보관합니다.
 */
export const pendingProfileFormCache = new Map<string, ProfileFormSnapshot>();

export function readPendingProfileFormFromSession(accountKey: string): ProfileFormSnapshot | null {
	try {
		const raw = sessionStorage.getItem(PENDING_PROFILE_FORM_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as PendingProfileFormSnapshot;
		if (parsed.accountKey !== accountKey) return null;
		if (!parsed.form || typeof parsed.form !== 'object') return null;
		return parsed.form;
	} catch {
		return null;
	}
}

export function readProfileFormFallbackFromLocal(accountKey: string): ProfileFormSnapshot | null {
	try {
		const raw = localStorage.getItem(PROFILE_FORM_FALLBACK_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as PendingProfileFormSnapshot;
		if (parsed.accountKey !== accountKey) return null;
		if (!parsed.form || typeof parsed.form !== 'object') return null;
		return parsed.form;
	} catch {
		return null;
	}
}

export function writePendingProfileFormToSession(accountKey: string, form: ProfileFormSnapshot): void {
	try {
		sessionStorage.setItem(PENDING_PROFILE_FORM_STORAGE_KEY, JSON.stringify({ accountKey, form }));
	} catch {}
}

export function writeProfileFormFallbackToLocal(accountKey: string, form: ProfileFormSnapshot): void {
	try {
		localStorage.setItem(PROFILE_FORM_FALLBACK_STORAGE_KEY, JSON.stringify({ accountKey, form }));
	} catch {}
}

export function clearPendingProfileFormFromSession(accountKey: string): void {
	try {
		const raw = sessionStorage.getItem(PENDING_PROFILE_FORM_STORAGE_KEY);
		if (!raw) return;
		const parsed = JSON.parse(raw) as PendingProfileFormSnapshot;
		if (parsed.accountKey === accountKey) {
			sessionStorage.removeItem(PENDING_PROFILE_FORM_STORAGE_KEY);
		}
	} catch {
		sessionStorage.removeItem(PENDING_PROFILE_FORM_STORAGE_KEY);
	}
}

export function clearProfileFormFallbackFromLocal(accountKey: string): void {
	try {
		const raw = localStorage.getItem(PROFILE_FORM_FALLBACK_STORAGE_KEY);
		if (!raw) return;
		const parsed = JSON.parse(raw) as PendingProfileFormSnapshot;
		if (parsed.accountKey === accountKey) {
			localStorage.removeItem(PROFILE_FORM_FALLBACK_STORAGE_KEY);
		}
	} catch {
		localStorage.removeItem(PROFILE_FORM_FALLBACK_STORAGE_KEY);
	}
}

export function buildProfileFormSnapshot(
	profile: ProfileLike,
	fallback: ProfileFormSnapshot | null = null,
): ProfileFormSnapshot {
	const profileImage =
		profile.profileImageUrl && profile.profileImageUrl.trim() !== ''
			? profile.profileImageUrl
			: fallback?.profileImage ?? null;

	return {
		realName: profile.realName?.trim() ?? fallback?.realName ?? '',
		countryId: profile.countryId ?? fallback?.countryId ?? null,
		phoneNumber: profile.phoneNumber?.trim() ?? fallback?.phoneNumber ?? '',
		affiliation: profile.affiliation?.trim() ?? fallback?.affiliation ?? '',
		position: profile.position?.trim() ?? fallback?.position ?? '',
		department: profile.department?.trim() ?? fallback?.department ?? '',
		jobTitle: profile.jobTitle ?? fallback?.jobTitle ?? null,
		customJobTitle: profile.customJobTitle?.trim() ?? fallback?.customJobTitle ?? '',
		profileImage,
	};
}

/** 서버 응답만 기준으로 AuthContext에 반영할 값을 만듭니다. */
export function buildApiProfileSnapshot(profile: ProfileLike): ProfileFormSnapshot {
	return {
		realName: profile.realName?.trim() ?? '',
		countryId: profile.countryId ?? null,
		phoneNumber: profile.phoneNumber?.trim() ?? '',
		affiliation: profile.affiliation?.trim() ?? '',
		position: profile.position?.trim() ?? '',
		department: profile.department?.trim() ?? '',
		jobTitle: profile.jobTitle ?? null,
		customJobTitle: profile.customJobTitle?.trim() ?? '',
		profileImage:
			profile.profileImageUrl && profile.profileImageUrl.trim() !== ''
				? profile.profileImageUrl
				: null,
	};
}

/** API 프로필을 아직 받지 못했거나 전환 계정일 때 사용할 화면 기본값입니다. */
export function buildAuthProfileFallback(
	user: {
		name?: string | null;
		profileImageUrl?: string | null;
	} | null | undefined,
	currentAccount: {
		organizationName?: string | null;
	} | null | undefined,
): ProfileFormSnapshot {
	return {
		realName: user?.name?.trim() ?? '',
		countryId: null,
		phoneNumber: '',
		affiliation: currentAccount?.organizationName?.trim() ?? '',
		position: '',
		department: '',
		jobTitle: null,
		customJobTitle: '',
		profileImage:
			user?.profileImageUrl && user.profileImageUrl.trim() !== ''
				? user.profileImageUrl
				: null,
	};
}

export function getImageFileExtension(type: string): string {
	if (type === 'image/png') return 'png';
	if (type === 'image/webp') return 'webp';
	if (type === 'image/gif') return 'gif';
	return 'jpg';
}

export function getProfileApiLanguageCode(language: string | undefined): string {
	const value = language?.trim().toLowerCase().split('-')[0] ?? '';
	if (value === 'ko' || value === 'en') return value;
	return 'en';
}

/** 프로필 국가가 비어 있을 때 기본 선택값으로 사용할 브라우저 지역 코드를 추론합니다. */
export function getBrowserCountryCode(): string {
	try {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const countryCode = timeZone ? TIME_ZONE_COUNTRY_CODE_BY_ID[timeZone] : undefined;
		if (countryCode) return countryCode;
	} catch {}

	const languageList = typeof navigator === 'undefined'
		? []
		: [
			...(navigator.languages ?? []),
			navigator.language,
		].filter(Boolean);

	for (const language of languageList) {
		try {
			const region = new Intl.Locale(language).region;
			if (region) return region.toUpperCase();
		} catch {}

		const match = language.match(/[-_]([A-Za-z]{2}|\d{3})\b/);
		if (match) return match[1].toUpperCase();

		const countryCode = LANGUAGE_COUNTRY_CODE_BY_LANGUAGE[language.toLowerCase().split('-')[0]];
		if (countryCode) return countryCode;
	}

	return '';
}

export function toProfileString(value: string): string {
	return value.trim();
}

export function isAbsoluteHttpOrHttpsUrl(url: string | null | undefined): url is string {
	if (!url) return false;
	try {
		const parsedUrl = new URL(url);
		return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
	} catch {
		return false;
	}
}
